
import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, map, Subject, takeUntil, tap } from 'rxjs';
import { PointLeaderboardService, RankingsResponse, RatingFilterConfig, RoundingRule, SeasonsResponse, Standing, TimelineResponse, User, PlayerCategory, ParentRoleFilter, ClassificationFilter, NON_CLASSIFIED_VALUE, isNonClassified, DEFAULT_PLAYER_CATEGORY } from '../../utilities/services/point-leaderboard/point-leaderboard.service';
import { CategoryLevel } from '../../utilities/services/point-leaderboard/point-leaderboard.service';
import { Category } from '../../utilities/services/point-leaderboard/point-leaderboard.service';
// import { PlayerRanking } from '../point-leaderboard/point-leaderboard.component';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { BreakdownSidebarComponent } from "./breakdown-sidebar/breakdown-sidebar.component";
import { BreakdownFilterSidebarComponent } from "./breakdown-filter-sidebar/breakdown-filter-sidebar.component";
import { LeaderboardSidebarComponent } from '../ranking/leaderboardsidebar/leaderboardsidebar.component';
import { environment } from '../../../environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { RoleServiceService } from '../../utilities/services/role-service/role-service.service';
import { SkeletonLoaderComponent } from '../shared/skeleton-loader/skeleton-loader.component';
import { RequestTracker } from '../../utilities/services/loading/request-tracker';
import { TemplateService } from '../../utilities/services/template/template.service';

interface CustomEvent {
  event_id: number;
  event_name: string;
  checked?: boolean
}

/** A Player-role dropdown profile field (e.g. Para) offered as a filter. */
export interface ClassificationFieldDef {
  name: string;
  values: string[];
}

export interface PlayerRating {
  rank: number;
  player: string;
  clubMa: string;
  parents?: { name: string; role_id: number }[];
  points: number;
  breakdown: string;
  movementIndicator: string;
  is_paused: boolean;
  player_profile: string | null;
  country_flag: string;
  player_id: number;
  event_points: { [eventId: string]: number };
  /** World Rank value sent by the backend; null = unranked internationally. */
  world_rank?: number | null;
}


@Component({
  selector: 'stupa-rating-point-leaderboard',
  standalone: true,
  imports: [
    SkeletonLoaderComponent,
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    // GifLoaderComponent,
    BreakdownSidebarComponent,
    BreakdownFilterSidebarComponent
],
  templateUrl: './rating-leaderboard.component.html',
  styleUrl: './rating-leaderboard.component.scss',
})
export class RatingLeaderboardComponent implements OnInit, AfterViewChecked, AfterViewInit, OnDestroy
{
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly leaderboardService = inject(PointLeaderboardService);
  private readonly roleService = inject(RoleServiceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly templateService = inject(TemplateService);
  private readonly unsubscribe$ = new Subject<void>();

  /** True when the OTT layout (template 1) is active — see {@link TemplateService}. */
  get isTemplate1(): boolean {
    return this.templateService.isTemplate1;
  }
  commonStorageUrl = environment.common_storage_url ?? 'https://storage.googleapis.com/stupaprodsguscentral/';
  podiumBackgroundNgStyle = {
    'background-image': `var(--rating-leaderboard-podium-image, url(${this.commonStorageUrl}/ettu-website-wordpress/2025/04/3bccfb9e7a230e47eca5560d1e7aaad2.png))`,
  };

  // Data for levels and categories
  levels: CategoryLevel[] = [];
  selectedLevel: string = '';
  categories: Category[] = [];
  selectedCategory: string = '';
  isDropdownOpen: boolean = false;
  selectedCategoryId: number | null = null;

  // Sidebar visibility
  isSidebarOpen: boolean = false;
  isBreakdownSidebarOpen: boolean = false;
  selectedParticipantId: number | null = null;
  isFilterSidebarOpen: boolean = false; // Added for filter sidebar
  selectedPlayerForBreakdown: any = null;

  // Rating data
  ratings: any[] = [];
    parents: number[] = [];
  rank_range: string[] = [];
  period: { value: string } | null = null;
  event_ids: number[] = [];
  genders: string[] = [];
  ages: number[] = [];
  category_id: number | null = null;
  player_category: PlayerCategory | null = DEFAULT_PLAYER_CATEGORY;
  // Snapshot-based row filters (PRD OTT leaderboard filters). Parent-role
  // selections travel as parent_role_filters (grouped per role) so RNR can
  // match publish-time snapshots and enforce the admin config; global ranks
  // are never recomputed by these.
  parent_role_filters: ParentRoleFilter[] = [];
  classification_filters: ClassificationFilter[] = [];
  classificationFields: ClassificationFieldDef[] = [];
  unfilteredTotalCount: number | null = null;

  appliedFilters: any = {};

  // Search term and debouncing
  searchTerm: string = '';
  private readonly searchSubject: Subject<string> = new Subject();

  // Pagination data
  currentPage: number = 1;
  itemsPerPage: number = 50;
  totalPages: number = 1;
  totalItems: number = 0;
  itemsPerPageOptions: number[] = [10, 20, 30, 40, 50, 100];

  // Sorting data
  sortBy: string = '';
  sortOrder: 'asc' | 'desc' = 'asc';

  // Preloaded filter data
  clubs: User[] = [];
  seasons: SeasonsResponse['seasons'] = [];
  events: CustomEvent[] = [];
  timeline: TimelineResponse | null = null;
  selectedSeasonId: string = '';
  selectedTimelineId: string | null = null;
  timelineSeasonId: string = '';
  publishedDates:string[] = [];
  selectedCategoryLevelId: number | null = null;
  selectedCategoryLevelName: string | null = null;

  // Loader for dynamic standings API
  _showLoader: boolean = false;
  readonly ratingsLoading = new RequestTracker();

  get showRatingSkeleton(): boolean {
    return this._showLoader || this.ratingsLoading.loading();
  }

  filterConfig:RatingFilterConfig = {
      enable_gender_filter: false,
      enable_age_filter: false,
      player_association_roles: [],
      ott_leaderboard: { enable_rating_period: false, enabled_classification_fields: [] },
    }

  parent_role_list: any[] = [];
  playerRoleId: any;
  clubsRoleId: number | null = null;

  private roundingRule: RoundingRule | null = null;

  isAlternateRatingEnabled: boolean = false;
  alternateRatingName : string = '';
  worldRankEnabled: boolean = false;
  worldRankAbbreviation: string = 'WR';
  worldRankName: string = 'World Rank';
  /** 'FEDERATION' = domestic order (default), 'WORLD' = World Rank first. */
  worldRankPriority: string = 'FEDERATION';

  /** True when the admin asked for the leaderboard to lead with World Rank. */
  get isWorldRankPriority(): boolean {
    return this.worldRankEnabled && this.worldRankPriority === 'WORLD';
  }

  // Reference to the category list container
  @ViewChild('categoryList', { static: false })
  categoryList!: ElementRef<HTMLDivElement>;

  // Reference to the sidebar component
  @ViewChild(LeaderboardSidebarComponent, { static: false })
  sidebarComponent!: LeaderboardSidebarComponent;

  // Track if scrolling is needed
  private needsScroll: boolean = false;

  // Podium related properties - Always true for expanded view
  isExpanded: boolean = true;

  ratingListLastUpdatedAt: string | null = null;
  selectedGender:number =1

  // Get event names for filtered event IDs
  // getFilteredEventNames(): { event_id: number; event_name: string }[] {
  //   return this.filterEventIds
  //     .map((id) => this.events.find((event) => event.event_id === id))
  //     .find((event): event is CustomEvent => !!event);
  // }

  // Check if any filters are applied
  // get areFiltersApplied(): boolean {
  //   return (
  //     this.filterClubIds.length > 0 ||
  //     this.filterRankMin !== undefined ||
  //     this.filterRankMax !== undefined ||
  //     this.dateFrom !== undefined ||
  //     this.dateTo !== undefined ||
  //     this.filterEventIds.length > 0 ||
  //     (this.filterSeasonId !== '' && this.filterSeasonId !== this.selectedSeasonId)
  //   );
  // }

  ngOnInit(): void {
    this.getRole();
    // Set up search debouncing
    this.searchSubject
      .pipe(tap(() => {
              this._showLoader=true;
            }),debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.searchTerm = searchTerm;
        this.fetchRatingLeaderboard();
      });
      this.loadConfig();
      this.fetchRatingSettings();
      this.fetchWorldRankSettings();
  }

  private formatDateForApi(date: Date): string {
    // Format date as YYYY-MM-DD
    return date.toISOString().split('T')[0];
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  fetchRatingSettings(): void {
  this.leaderboardService.getRatingSettings().pipe(this.ratingsLoading.track()).subscribe({
    next: (response) => {
      this.roundingRule = response.rounding_rule;
    },
    error: (err) => {
      }
  });
}


  private fetchWorldRankSettings(): void {
    this.leaderboardService
      .getWorldRankSettings()
      .pipe(this.ratingsLoading.track(), takeUntil(this.unsubscribe$))
      .subscribe({
        next: (settings) => {
          this.worldRankEnabled = !!settings?.world_rank_enabled;
          this.worldRankAbbreviation = settings?.rank_abbreviation || 'WR';
          this.worldRankName = settings?.rank_name || 'World Rank';
          this.worldRankPriority = settings?.prioritize_rank || 'FEDERATION';
          this.cdr.detectChanges();
          if (this.worldRankEnabled) {
            // Priority decides the default order: World Rank when the admin picked
            // International, rating otherwise. Backend sorts, so paging stays right.
            this.sortBy = this.isWorldRankPriority ? 'world_rank' : 'rating';
            this.sortOrder = 'asc';
          }
          this.fetchRatingLeaderboard();
        },
        error: () => {
          this.worldRankEnabled = false;
          this.fetchRatingLeaderboard();
        },
      });
  }

  private applyRounding(value: number): number {
    if (!this.roundingRule || typeof value !== 'number') {
      return value; // Return original value if no rule is set or value is not a number
    }

    const { function: func, decimal_places } = this.roundingRule;
    const multiplier = Math.pow(10, decimal_places || 0);

    if (func === 'floor') {
      return Math.floor(value * multiplier) / multiplier;
    } else if (func === 'ceiling') {
      return Math.ceil(value * multiplier) / multiplier;
    } else {
      // Default to standard rounding if function is not 'floor' or 'ceil'
      return Math.round(value * multiplier) / multiplier;
    }
  }

  loadConfig(): void {
    this.leaderboardService.getFilterConfig().pipe(this.ratingsLoading.track()).subscribe({
      next: (data) => {
        this.filterConfig = data;
        // Configs saved before the OTT leaderboard filters existed lack the
        // nested block; default it so template bindings stay safe.
        if (!this.filterConfig.ott_leaderboard) {
          this.filterConfig.ott_leaderboard = { enable_rating_period: false, enabled_classification_fields: [] };
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching filter config:', err);
      },
    });
  }

  // The Player role's dropdown profile fields (e.g. Para) with their values in
  // value_json dataN order, prefixed with the NON_CLASSIFIED sentinel so the
  // non-classified cohort is a normal selectable value (PRD v4.1) rather than
  // an always-visible exception. The sidebar shows the subset enabled in the
  // tenant's filter configuration.
  loadClassificationFieldDefs(): void {
    if (!this.playerRoleId) return;
    this.roleService
      .getRoleFields(this.playerRoleId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const body = res.data;
          if (body?.code === 200) {
            const roleFields = Array.isArray(body?.data) && body.data.length > 1 ? body.data[1] : [];
            const defs: ClassificationFieldDef[] = [];
            const seen = new Set<string>();
            // Reserved structured fields (dedicated filters exist) are never
            // classification tabs, even if a stale config still lists them.
            const reserved = new Set(['gender', 'citizen type', 'country', 'name', 'full name', 'd.o.b', 'dob', 'date of birth']);
            roleFields.forEach((rf: any) => {
              const field = rf.field;
              if (field?.type !== 'dropdown' || !field?.value_json || seen.has(field.name)) return;
              if (reserved.has((field.name || '').toLowerCase())) return;
              const classes = Object.keys(field.value_json)
                .sort((a, b) => Number(a.replace('data', '')) - Number(b.replace('data', '')))
                .map((k) => field.value_json[k])
                // Case/whitespace-insensitive: a tenant value_json holding
                // 'none' or ' None' must collapse into the single sentinel row,
                // not render a second checkbox RNR would never match.
                .filter((v: any) => typeof v === 'string' && v.trim() !== '' && !isNonClassified(v));
              if (classes.length > 0) {
                seen.add(field.name);
                // Sentinel first and exactly once — it is also the wire value
                // RNR matches the non-classified cohort on.
                defs.push({ name: field.name, values: [NON_CLASSIFIED_VALUE, ...classes] });
              }
            });
            this.classificationFields = defs;
            this.cdr.detectChanges();
          }
        },
        error: () => {},
      });
  }

  getRole(): void {
    this.roleService.getRoles().pipe(this.ratingsLoading.track()).subscribe({
      next: (res: any) => {
        if (res.data) {
          const roles = res.data.roles as Array<{ id: number; name: string }>;
          this.playerRoleId = roles.find((c) => c.name === 'Player')?.id;
          const maRole = roles.find((c) => c.name === 'Member Association');
          this.clubsRoleId = maRole?.id ?? null;
        }
      },
      error: (err: any) => console.error('Error fetching roles:', err),
      complete: () => {
        this.getRoleNames();
        this.loadClassificationFieldDefs();
      },
    });
  }

  getRoleNames(): void {
    if (!this.playerRoleId) return;
    this.roleService.get_roles_entity(this.playerRoleId).pipe(this.ratingsLoading.track()).subscribe({
      next: (res: any) => {
        this.parent_role_list = (res.data || [])
          .filter((x: any) => x.parent_role_id !== 1)
          .filter(
            (x: any) =>
              (x.parent_role_name || '').toLowerCase() !== 'admin',
          )
          .map((role: any) => ({
            ...role,
            parent_user_roles: (role.parent_user_roles || []).filter(
              (item: any) =>
                item.parent_role_linked_field_value !== '' &&
                item.parent_role_linked_field_value != null,
            ),
          }));
        this.cdr.detectChanges();
      },
      error: (err: any) => console.error('Error fetching roles:', err),
    });
  }

  private getCurrentDate(): Date {
    return new Date();
  }

  getFilteredEventNames(): { event_id: number; event_name: string }[] {
      return this.event_ids
        .map((id) => this.events.find((event) => event.event_id === id))
        .filter((event): event is CustomEvent => !!event);
  }


  private fetchTimeline(categoryId: number): void {
    if (!this.selectedSeasonId) return;
    this.leaderboardService
      .getRankingTimeline(Number(this.selectedSeasonId), categoryId)
      .pipe(this.ratingsLoading.track())
      .subscribe((response) => {
        this.timeline = response;
        this.timelineSeasonId = this.selectedSeasonId;
        const timelineArray = this.getTimelineArray(response);
        const latestCompleted = timelineArray.find(
          (item) => item.status === 'completed'
        );
        this.selectedTimelineId = latestCompleted
          ? latestCompleted.ranking_list_id
          : null;
        this.cdr.detectChanges();
      });
  }

  private updateEvents(response: RankingsResponse, searchValue: string): void {
    const today = this.getCurrentDate();
    const eventMap: {
      [key: number]: {
        event_id: number;
        event_name: string;
        hasActivePoints: boolean;
      };
    } = {};

    response.standings.forEach((standing) => {
      standing.event_breakdown.forEach((event) => {
        const expiryDate = new Date(event.expiry_date);
        const isActive = expiryDate >= today;
        if (!eventMap[event.event_id]) {
          eventMap[event.event_id] = {
            event_id: event.event_id,
            event_name: event.event_name,
            hasActivePoints: isActive,
          };
        } else if (isActive) {
          eventMap[event.event_id].hasActivePoints = true;
        }
      });
    });

    let events = Object.values(eventMap)
      .filter((event) => event.hasActivePoints)
      .map((event) => ({
        event_id: event.event_id,
        event_name: event.event_name,
      }));

    if (searchValue) {
      events = events.filter((event) =>
        event.event_name.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    this.events = events;
  }

  private getTimelineArray(response: TimelineResponse): any[] {
    switch (response.configure) {
      case 'at_the_end_of_every_event':
      case 'day':
        return response.by_date;
      case 'month':
        return response.by_month;
      case 'week':
        return response.by_week;
      default:
        return [];
    }
  }

  ngAfterViewChecked(): void {
    if (this.needsScroll && this.categoryList) {
      this.scrollToActiveCategory();
      this.needsScroll = false;
    }
  }

 private fetchCategoryLevels(): void {
  this.leaderboardService
    .getCategoryLevels()
    .pipe(this.ratingsLoading.track(),
      map((levels: CategoryLevel[]) =>
        levels.map((level) => ({
          ...level,
          level: this.capitalize(level.level),
        }))
      )
    )
    .subscribe({
      next: (levels: CategoryLevel[]) => {
        this.levels = levels;
        this.activatedRoute.params.subscribe((params) => {
          const levelId = params['levelId'];
          const categoryId = params['categoryId'];
          
          if (levelId) {
            const selected = this.levels.find(
              (level) => level.id === +levelId
            );
            this.selectedLevel = selected
              ? selected.level
              : this.levels[0]?.level || '';
            this.fetchCategories(+levelId, categoryId);
          } else {
            this.selectedLevel = this.levels[0]?.level || '';
            if (this.levels[0]?.id) {
              
              this.router.navigate(['/ranking', this.levels[0].id]); // Updated route
            }
          }
          this.cdr.detectChanges();
          
        });
      },
      error: (error) => {
        console.error('Failed to fetch levels:', error);
        this.levels = [];
        this.selectedLevel = '';
        this.categories = [];
        this.cdr.detectChanges();
      },
    });
}

  private fetchCategories(levelId: number, categoryId?: string): void {
    this.leaderboardService.getCategories().pipe(this.ratingsLoading.track()).subscribe({
      next: (categories: Category[]) => {
        this.categories = categories.filter(
          (category) => category.category_level_id === levelId
        );
        
        if (categoryId) {
          const selected = this.categories.find(
            (category) => category.id === +categoryId
          );
          this.selectedCategory = selected
            ? selected.category_description
            : this.categories[0]?.category_description || '';
          this.selectedCategoryId = selected
            ? selected.id
            : this.categories[0]?.id || null;
        } else {
          this.selectedCategory =
            this.categories[0]?.category_description || '';
          this.selectedCategoryId = this.categories[0]?.id || null;
          if (this.categories[0]?.id) {
            
            this.router.navigate([
              '/ranking',
              levelId,
              this.categories[0].id,
            ]);
          }
        }
        this.needsScroll = true;
        this.updateScrollArrows();
        if (categoryId || this.categories[0]?.id) {
          const selectedCategoryId = categoryId
            ? +categoryId
            : this.categories[0].id;
          if (this.selectedSeasonId) {
            this.leaderboardService
              .getRankings(
                Number(this.selectedSeasonId),
                selectedCategoryId,
                1,
                100, // Fetch enough items for podium to always have top 3 if available
                [],
                undefined,
                undefined,
                undefined,
                undefined,
                this.searchTerm,
                this.sortBy,
                this.sortOrder,
                []
              )
              .pipe(this.ratingsLoading.track())
              .subscribe((response) => {
                this.cdr.detectChanges();
              });
            this.timelineSeasonId = this.selectedSeasonId;
            this.fetchTimeline(selectedCategoryId);
          } else {
            console.warn(
              'No season ID available, skipping events and timeline fetch.'
            );
            this._showLoader = false;
            this.cdr.detectChanges();
          }
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Failed to fetch categories:', error);
        this.categories = [];
        this.selectedCategory = '';
        this.selectedCategoryId = null;
        this.totalPages = 1;
        this.totalItems = 0;
        this.currentPage = 1;
        this._showLoader = false;
        this.cdr.detectChanges();
      },
    });
  }


  toggleSort(): void {
    //   this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    // } else {
    //   this.sortBy = column;
    //   this.sortOrder = 'asc';
    // }
    this.sortBy = 'rating'
    this.sortOrder = this.sortOrder == 'asc' ? 'desc' : 'asc';
    this.fetchRatingLeaderboard()
  }

  toggleSortAlternateRating(){
    this.sortBy = 'alternate_ranking'
    this.sortOrder = this.sortOrder == 'asc' ? 'desc' : 'asc';
    this.fetchRatingLeaderboard()
  }

  /** Same asc/desc toggle the admin World Rank table uses (sort_by: 'world_rank'). */
  toggleSortWorldRank(): void {
    this.sortBy = 'world_rank';
    this.sortOrder = this.sortOrder == 'asc' ? 'desc' : 'asc';
    this.fetchRatingLeaderboard();
  }

  onSearch(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onApplyFilters(event: {
    filterClubIds: number[];
    filterRankMin?: number;
    filterRankMax?: number;
    dateFrom?: string;
    dateTo?: string;
    filterEventIds?: number[];
    filterSeasonId?: string;
  }) {
    const levelId = this.levels.find((l) => l.level === this.selectedLevel)?.id;
    const categoryId = this.categories.find(
      (c) => c.category_description === this.selectedCategory
    )?.id;
    if (levelId && categoryId) {
      this.currentPage = 1;
    }
    this.isSidebarOpen = false;
    this.cdr.detectChanges();
  }

  toggleSidebar(): void {
    this.isFilterSidebarOpen = true;
    this.cdr.detectChanges();
  }

  closeSidebar(): void {
    this.isFilterSidebarOpen = false;
    this.cdr.detectChanges();
  }

  onBreakdownClick(rating: PlayerRating): void {
    
    if (rating.player_id) {
      this.selectedParticipantId = rating.player_id;
      this.isBreakdownSidebarOpen = true;
      this.selectedPlayerForBreakdown = rating
      this.cdr.detectChanges();
    }
  }

  closeBreakdownSidebar(): void {
    this.isBreakdownSidebarOpen = false;
    this.selectedParticipantId = null;
    this.cdr.detectChanges();
  }

  onPauseStatusChanged(): void {
    this.closeBreakdownSidebar();
    this.cdr.detectChanges();
  }

  private capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  // goBack() {
  //   this.router.navigate(['admin', 'manage', 'ranking-settings']);
  // }

  // goTo(path: string): void {
  //   switch (path) {
  //     case 'manage':
  //       this.router.navigate(['/admin/manage']);
  //       break;
  //     case 'select-point':
  //       if (
  //         globalThis.window !== undefined &&
  //         globalThis.window.localStorage !== undefined
  //       ) {
  //         localStorage.setItem('fromBreadcrumb', 'true');
  //       }
  //       this.router.navigate(['/admin/manage/ranking-rating']);
  //       break;
  //     case 'ranking-system':
  //       this.router.navigate(['/admin/manage/ranking-settings']);
  //       break;
  //   }
  // }

  toggleDropdown(event: Event) {
    event.stopPropagation();
    
    this.isDropdownOpen = !this.isDropdownOpen;
    
    this.cdr.detectChanges();
    setTimeout(() => {
      document.querySelector('.dropdown-menu');
      
    }, 0);
  }

  selectLevel(level: string) {
    
    this.selectedLevel = level;
    const selectedLevelObj = this.levels.find((l) => l.level === level);
    
    if (selectedLevelObj?.id) {
      this.router.navigate([
        '/ranking',
        selectedLevelObj.id,
      ]);
    }
    this.isDropdownOpen = false;
    this.cdr.detectChanges();
  }

  selectCategory(category: string) {
    
    this.selectedCategory = category;
    const selectedCategoryObj = this.categories.find(
      (c) => c.category_description === category
    );
    
    const levelId = this.levels.find((l) => l.level === this.selectedLevel)?.id;
    if (selectedCategoryObj?.id && levelId) {
      this.selectedCategoryId = selectedCategoryObj.id;
      
      this.router.navigate([
        '/ranking',
        levelId,
        selectedCategoryObj.id,
      ]);
      this.needsScroll = true;
    } else {
      
      this.selectedCategoryId = null;
    }
    this.updateScrollArrows();
    this.cdr.detectChanges();
  }

  onContainerClick(event: Event) {
    const target = event.target as HTMLElement;
    const isLevelClick = target.closest('.level');
    const isSidebarClick = target.closest('stupa-leaderboard-sidebar');
    const isBreakdownSidebarClick = target.closest('stupa-point-breakdown');
    const isExpiredPointsSidebarClick = target.closest('stupa-expired-points');
    const isEditSidebarClick = target.closest('stupa-edit-sidebar');
    const isVictoryPointOverlayClick = target.closest('.victory-point-overlay');
    const isFilterButtonClick = target.closest('.filters-btn');
    const isBreakdownButtonClick = target.closest('.breakdown-btn');

    if (!isLevelClick && this.isDropdownOpen) {
      this.isDropdownOpen = false;
      this.cdr.detectChanges();
    }
    if (
      !isSidebarClick &&
      !isFilterButtonClick &&
      this.isSidebarOpen &&
      !isBreakdownSidebarClick &&
      !isExpiredPointsSidebarClick &&
      !isEditSidebarClick &&
      !isVictoryPointOverlayClick
    ) {
      this.isSidebarOpen = false;
      this.cdr.detectChanges();
    }
    if (
      !isBreakdownSidebarClick &&
      !isExpiredPointsSidebarClick &&
      !isBreakdownButtonClick &&
      this.isBreakdownSidebarOpen &&
      !isEditSidebarClick &&
      !isVictoryPointOverlayClick
    ) {
      this.closeBreakdownSidebar();
    }
  }

  scrollCategories(direction: 'left' | 'right') {
    const categoryList = this.categoryList?.nativeElement;
    if (categoryList) {
      const scrollAmount = 200;
      categoryList.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
      setTimeout(() => this.updateScrollArrows(), 300);
    }
  }

  private scrollToActiveCategory(): void {
    if (!this.categoryList || !this.selectedCategory) {
      console.warn(
        'scrollToActiveCategory: categoryList or selectedCategory is undefined'
      );
      return;
    }

    const container = this.categoryList.nativeElement;
    const categoryElements = container.querySelectorAll(
      '.category-item'
    ) as NodeListOf<HTMLElement>;
    

    const index = this.categories.findIndex(
      (c) => c.category_description === this.selectedCategory
    );
    

    if (index >= 0 && categoryElements[index]) {
      try {
        categoryElements[index].scrollIntoView({
          behavior: 'smooth',
          inline: 'start',
        });
        
      } catch (error) {
        console.warn('scrollIntoView failed, using scrollTo:', error);
        const element = categoryElements[index] as HTMLElement;
        const elementOffset = element.offsetLeft;
        container.scrollTo({ left: elementOffset, behavior: 'smooth' });
        
      }
      setTimeout(() => {
        this.updateScrollArrows();
        
      }, 300);
    } else {
      console.warn(
        'scrollToActiveCategory: Category element not found at index:',
        index,
        'Total elements:',
        categoryElements.length
      );
    }
  }

  private updateScrollArrows(): void {
    const container = this.categoryList?.nativeElement;
    if (!container) return;

    const leftArrow = document.querySelector(
      '.scroll-btn.left'
    ) as HTMLButtonElement;
    const rightArrow = document.querySelector(
      '.scroll-btn.right'
    ) as HTMLButtonElement;
    const maxScroll = container.scrollWidth - container.clientWidth;

    if (leftArrow && rightArrow) {
      leftArrow.disabled = container.scrollLeft <= 0;
      rightArrow.disabled = container.scrollLeft >= maxScroll - 1;
      leftArrow.style.opacity = leftArrow.disabled ? '0.5' : '1';
      rightArrow.style.opacity = rightArrow.disabled ? '0.5' : '1';
      
    }
  }

  getStartIndex(): number {
    return this.totalItems > 0 ? (this.currentPage - 1) * this.itemsPerPage + 1 : 0;
  }

  getEndIndex(): number {
    const end = this.currentPage * this.itemsPerPage;
    return Math.min(end, this.totalItems);
  }

  onItemsPerPageChange(): void {
    this.itemsPerPage = Number(this.itemsPerPage);
    this.currentPage = 1;
    this.fetchRatingLeaderboard();
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.fetchRatingLeaderboard();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.fetchRatingLeaderboard();
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.fetchRatingLeaderboard();
    }
  }

  goToFirstPage(): void {
    if (this.currentPage !== 1) {
      this.currentPage = 1;
      this.fetchRatingLeaderboard();
    }
  }

  goToLastPage(): void {
    if (this.currentPage !== this.totalPages) {
      this.currentPage = this.totalPages;
      this.fetchRatingLeaderboard();
    }
  }

  getPageNumbers(): number[] {
    const pages = [];
    const maxPagesToShow = 3;
    let startPage = Math.max(1, this.currentPage - 1);
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  // --- Podium-related methods ---

  // Removed toggleExpand() as it's not needed for always expanded view

  /**
   * Returns a suffix for a given rank (e.g., "st", "nd", "rd", "th").
   * @param rank The rank number.
   * @returns The appropriate suffix.
   */
  getRankSuffix(rank: number): string {
    if (rank === 1) return 'st';
    if (rank === 2) return 'nd';
    if (rank === 3) return 'rd';
    return 'th';
  }

  /**
   * Returns a color code based on the rank (gold for 1st, silver for 2nd, green for 3rd).
   * @param rank The rank number.
   * @returns The hex color code.
   */
  getRankColor(rank: number): string {
    if (rank === 1) return '#E1CE60'; // Gold-like
    if (rank === 2) return '#E18560'; // Silver-like (using a different shade)
    if (rank === 3) return '#7DAD3A'; // Green for bronze
    return '#000000'; // Default color for other ranks (black)
  }

  /**
   * Returns the top three players from the `rankings` array.
   * This is used for the condensed podium display.
   * @returns An array containing the top three players.
   */
  getTopThreePlayers(): PlayerRating[] {
    // Ensure rankings are sorted by rank before slicing
    const sortedRankings = [...this.ratings].sort((a, b) => a.rank - b.rank);
    return sortedRankings.slice(0, 3);
  }

  /**
   * Returns a specific player by their rank. Used for individual podium spots.
   * @param rank The rank of the player to retrieve.
   * @returns The PlayerRanking object or undefined if not found.
   */
  getPlayerByRank(rank: number): PlayerRating | undefined {
    return this.ratings.find(player => player.rank === rank);
  }

  /**
   * Returns an array of players who share a specific rank. Used for potential ties (e.g., 3rd place).
   * @param rank The rank to filter players by.
   * @returns An array of PlayerRanking objects.
   */
  getPlayersByRank(rank: number): PlayerRating[] {
    return this.ratings.filter(player => player.rank === rank);
  }

  /**
   * The player on the given podium spot (1 = winner cube, 2/3 = side cubes):
   * the Nth-best row of the CURRENT (possibly filtered) result set. Ranks are
   * intentionally not recomputed under row filters — the label shows the
   * player's global rank (PRD: filters hide rows, never re-rank).
   */
  getPodiumPlayer(position: number): PlayerRating | undefined {
    return this.getTopThreePlayers()[position - 1];
  }

  /**
   * Determines whether the podium should be displayed.
   * It checks if there are any rankings available with valid ranks.
   * @returns True if the podium should be shown, false otherwise.
   */
  showPodium(): boolean {
    // Hidden whenever a row filter (search / parent-role / classification) is
    // active, since a filtered result can't fill a meaningful top-3 podium.
    // The all-values-ticked default is NOT a filter — areFiltersApplied
    // excludes the default "Active" player-status view, so top-3-active
    // still shows on first load.
    return this.ratings && this.ratings.some(player => player.rank !== null) && !this.areFiltersApplied && this.searchTerm.trim().length==0 && !this._showLoader && this.ratings.length>2 && this.currentPage==1;
  }

    applyFilters(filters: any): void {
    // Store the complete filters object
    this.appliedFilters = filters;

    // Update state from the filter sidebar's output
    this.parents = filters.parents || [];
    this.rank_range = filters.rank_range || [];
    this.period = filters.period || null;
    this.event_ids = filters.event_ids || [];
    
    this.genders = filters.genders || [];
    this.ages = filters.ages || [];
    this.category_id = filters.category_id;
    this.player_category = filters.player_category ?? DEFAULT_PLAYER_CATEGORY;
    this.parent_role_filters = filters.parent_role_filters || [];
    this.classification_filters = filters.classification_filters || [];
    this.selectedCategoryLevelId = filters.level_id
    this.selectedCategoryLevelName = filters.level_name
    this.events = filters?.events?.filter((event:any)=>event?.checked);
    this.currentPage = 1;
    this.fetchRatingLeaderboard();
    this.isFilterSidebarOpen = false;
  }

  // Drives the outer "Filters" button highlight, the clear (X) visibility,
  // and podium eligibility. The default "Active" player-status view is
  // always sent to the API but is NOT itself a user-applied filter — same
  // all-ticked-default convention the classification/parent-role filters
  // use — so it's excluded here; only picking "Inactive" (or any other
  // filter) counts as applied.
  get areFiltersApplied(): boolean {
    return (
      this.parents.length > 0 ||
      this.rank_range.length > 0 ||
      this.period != null ||
      this.event_ids.length > 0  ||
      this.genders.length > 0 ||
      this.ages.length > 0 ||
      this.category_id != undefined ||
      (this.player_category != null && this.player_category !== DEFAULT_PLAYER_CATEGORY) ||
      this.parent_role_filters.length > 0 ||
      this.classification_filters.length > 0
    );
  }

  // A historical rating period is active when the selected publication date is
  // not the newest one (publishedDates arrives newest-first from the API).
  get isHistoricalPeriodActive(): boolean {
    return !!this.period?.value
      && this.publishedDates?.length > 0
      && this.period.value !== this.publishedDates[0];
  }

  // PRD 7.1 "Back to Latest": resets the period AND the row filters.
  backToLatest(): void {
    this.removeFilters();
  }

  // Row filters hide rows but never re-rank; when they cut the list down we
  // show "filtered from N" using the row-filter-free count from the API.
  get isRowFiltered(): boolean {
    return this.unfilteredTotalCount != null && this.unfilteredTotalCount !== this.totalItems;
  }


    fetchRatingLeaderboard(): void {
    this._showLoader = true;
    const payload: any = {
      tenant_id: environment.tenant_Name,
      page: this.currentPage,
      per_page: this.itemsPerPage,
      sort_by: this.sortBy,
      sort_order: this.sortOrder,
      gender_id:this.selectedGender
    };

    // Add filters to payload only if they have values
    if (this.searchTerm.trim()) {
      payload.search_name = this.searchTerm.trim();
    }
    // Parent-org selections travel as snapshot-based parent_role_filters when
    // available (point-in-time correct + admin-config enforced); the legacy
    // live-join `parents` param stays as fallback so nothing double-filters.
    if (this.parent_role_filters.length === 0 && this.parents.length > 0) {
      payload.parents = this.parents;
    }
    if (this.rank_range.length > 0) {
      payload.rank_range = this.rank_range;
    }
    if (this.period) {
      payload.period = this.period;
    }
    if (this.event_ids.length > 0) {
      payload.event_ids = this.event_ids;
    }
 
    if (this.ages.length > 0) {
      payload.ages = this.ages;
    }
    if (this.category_id !== null && this.category_id !== undefined) {
      payload.category_id = this.category_id;
    }
    if (this.player_category) {
      payload.player_category = this.player_category;
    }
    if (this.parent_role_filters.length > 0) {
      payload.parent_role_filters = this.parent_role_filters;
    }
    if (this.classification_filters.length > 0) {
      payload.classification_filters = this.classification_filters;
    }

    this.ratings = [];
    this.leaderboardService.getRatingLeaderboard(payload)
      // ... (rest of the subscription logic is the same)
      .pipe(this.ratingsLoading.track(), takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.totalItems = response.total_count || 0;
          this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
          

          this.ratings = response.items.map((item: any) => {
            let movementIndicator = ' ';
            if(item.previous_rank == null) movementIndicator = '+';
            else{
              const rank_difference = item.rank - item.previous_rank;
              if (rank_difference < 0) {
                movementIndicator = '+';
              } else if (rank_difference > 0) {
                movementIndicator = '-';
              }

              //   movementIndicator = '+';
              // } else if (item.rating_change < 0) {
              //   movementIndicator = '-';
              // }
            }


            const parents = item.parents || [];
            const displayParent =
              parents.find((p: any) => p.role_id !== 1) || parents[0];
            return {
              rank: item.rank,
              player: item.player_name,
              parents,
              clubMa: item.member_association || item.club || displayParent?.name || 'N/A',
              alternate_rating: this.applyRounding(item.alternate_rating),
              world_rank: item.world_rank ?? null,
              points: this.applyRounding(item.rating_points),
              status: item.status,
              movementIndicator: movementIndicator,
              player_id: item.player_id,
              player_profile: item?.player_profile? item?.player_profile: '../../../../assets/ranking-rating_assets/fallback_pfp.svg',
              country_flag:
                displayParent?.country_flag ||
                (item?.country_flag
                  ? item?.country_flag
                  : '../../../../assets/ranking-rating_assets/fallback_flag.svg'),
              is_paused: false,
              event_points: item?.event_points
            };
          });
          if(this.ratings.length>0){
            this.isAlternateRatingEnabled = response.items[0].alternate_rating_enabled;
            if(this.isAlternateRatingEnabled) this.alternateRatingName = response.items[0].alternate_rating_name
          }
          if(response.last_updated_at) this.ratingListLastUpdatedAt = response.last_updated_at;
          this._showLoader = false;
          this.publishedDates = response?.meta_data?.published_dates ?? [];
          this.unfilteredTotalCount = response?.meta_data?.unfiltered_total_count ?? null;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Failed to fetch rating leaderboard:', err);
          this.ratings = [];
          this.totalItems = 0;
          this.totalPages = 1;
          this._showLoader = false;
          this.cdr.detectChanges();
        }
      });
  }

  getDisplayableRoles(): any[] {
    return this.parent_role_list || [];
  }

  getParentValueForRole(rating: any, parentRoleId: number): string {
    const parents = rating?.parents || [];
    const match = parents.find((p: any) => p.role_id === parentRoleId);
    return match?.name || '';
  }

    public getGridTemplateColumns(): string {
    let columns : any[] = [];
    const roleCols = this.getDisplayableRoles();
    const parentColCount = roleCols.length > 0 ? roleCols.length : 1;

    if(this.isAlternateRatingEnabled){
      columns = [
      'minmax(60px, 0.5fr)',  // RANK
      'minmax(130px, 2fr)',   // PLAYER
    ];
    for (let i = 0; i < parentColCount; i++) {
      columns.push('minmax(150px, 2fr)');
    }
    columns.push('minmax(80px, 1.5fr)', 'minmax(80px, 1.5fr)');
    }
    else{
      columns = [
      'minmax(60px, 0.5fr)',  // RANK
      'minmax(130px, 2fr)',   // PLAYER
    ];
    for (let i = 0; i < parentColCount; i++) {
      columns.push('minmax(150px, 2fr)');
    }
    columns.push('minmax(80px, 1.5fr)');
    }
    if (this.worldRankEnabled) {
      columns.unshift('minmax(80px, 0.5fr)');
    }

    // Add a column for each filtered event
    if (this.event_ids && this.event_ids.length > 0) {
      
      this.event_ids.forEach(event => {
        columns.push('minmax(120px, 1fr)'); // Width for the dynamic event column
      });
    }

    columns.push('minmax(100px, 1fr)'); // BREAKDOWN
    
    return columns.join(' ');
  }

  ratingPointsExist(rating:PlayerRating, event: CustomEvent):boolean {
    
    if(rating.event_points && event.event_id.toString() in rating.event_points) return true;
    return false
  }

  removeFilters(){
    this.appliedFilters ={
    parents: [],
    rank_range: undefined,
    period: undefined,
    event_ids: [],
    genders: [],
    ages: [],
    category_id: undefined,
    level_id: undefined,
    player_category: DEFAULT_PLAYER_CATEGORY,
    parent_role_filters: [],
    classification_filters: [],
  };

    this.parent_role_filters = [];
    this.classification_filters = [];
    this.parents = this.appliedFilters.parents || [];
    this.rank_range = this.appliedFilters.rank_range || [];
    this.period = this.appliedFilters.period || null;
    this.event_ids = this.appliedFilters.event_ids || [];
    this.genders = this.appliedFilters.genders || [];
    this.ages = this.appliedFilters.ages || [];
    this.category_id = this.appliedFilters.category_id;
    this.player_category = this.appliedFilters.player_category ?? DEFAULT_PLAYER_CATEGORY;
    this.selectedCategoryLevelId = this.appliedFilters.level_id
    this.selectedCategoryLevelName = this.appliedFilters.level_name
    this.events = this.appliedFilters?.events?.filter((event:any)=>event?.checked);
    this.fetchRatingLeaderboard()
  }

  selectGenderTab(genderId: 1 | 2): void {
    if (this.selectedGender === genderId) {
      return;
    }
    this.selectedGender = genderId;
    this.currentPage = 1;
    this.fetchRatingLeaderboard();
  }

  

  trackById(index: number, item: unknown): string | number {
    if (item != null && typeof item === 'object' && 'id' in item) {
      const id = (item as { id?: string | number }).id;
      if (id != null) return id;
    }
    return index;
  }
}